/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UpdateModule
 */

import {
    Component,
    EventEmitter,
    Inject,
    OnInit,
    Output,
} from '@angular/core';

import {
    IEventMap,
    IReportDetail,
    IReportTask,
    IUpgradeEvent,
    UpgradeFsmState,
} from 'generated-types';

import { utc } from 'moment';

import {
    ISystemReportStartAndEndTime,
    ISystemReportSummary,
} from 'ng/modules/system-report-summary';

import {
    SystemReportsCollection,
} from 'ng/modules/collections/factories/system-reports.collection.factory';

import { TItemConfig } from 'object-type-item';

import './dry-run-report.component.less';

const MILLISECONDS = 'milliseconds';
const TASK_SUCCESS_STATUS = 'Success';

/**
 * @description
 *
 * A wrapper for System summary report for dry run.
 *
 * @author Deepak Chaudhary
 */
@Component({
    selector: 'dry-run-report',
    templateUrl: './dry-run-report.component.html',
})
export class DryRunReportComponent implements OnInit {
    /**
     * Emit the uuid for transcript report link.
     */
    @Output()
    public onReportIdChange = new EventEmitter<string>();

    public systemReportName: string;

    public systemReportStartAndEndTime: ISystemReportStartAndEndTime;

    public systemReadinessSummaryDetails: ISystemReportSummary;

    public dryRunSummaryDetails: ISystemReportSummary;

    public systemTaskDetails: ISystemReportSummary;

    public isReportLoaded = false;

    public constructor(
        @Inject(SystemReportsCollection)
        private readonly SystemReportCollectionClass: typeof SystemReportsCollection,
    ) { }

    /** @override */
    public ngOnInit(): void {
        this.setSystemReportSummary();
    }

    /**
     * Set the summary details for the system report.
     * This includes readiness, dry run, and task summaries,
     * along with setting the report's name and timestamps.
     */
    private async setSystemReportSummary(): Promise<void> {
        const systemReportCollection = new this.SystemReportCollectionClass();

        systemReportCollection.setParams({ sort: '_last_modified' });

        await systemReportCollection.load();

        const systemReport = systemReportCollection
            .items[systemReportCollection.items.length - 1]
            .dataToSave();

        this.systemReportName = systemReport?.name;
        this.onReportIdChange.emit(systemReport.uuid);

        this.systemReportStartAndEndTime = {
            startTime: systemReport?.readiness_reports?.[0]?.system_readiness?.start_time,
            endTime: systemReport
                ?.readiness_reports
                ?.[systemReport.readiness_reports.length - 1]
                ?.system_readiness?.end_time,
        };

        const summaryDetails = {
            errors: 0,
            success: 0,
            warnings: 0,
            totalTime: 0,
            maxProgressBarValue: 0,
        };

        this.systemReadinessSummaryDetails = { ...summaryDetails };
        this.dryRunSummaryDetails = { ...summaryDetails };
        this.systemTaskDetails = { ...summaryDetails };
        this.isReportLoaded = true;

        this.setSystemReadinessSummary(systemReport);
        this.setDryRunSummary(systemReport);
        this.setSystemTaskSummary(systemReport);

        await systemReportCollection.destroy();
    }

    /**
     * Calculate the summary of system readiness report.
     * The summary includes the number of success, warnings and errors and the total time taken.
     */
    private setSystemReadinessSummary(systemReport: TItemConfig): void {
        systemReport?.readiness_reports.forEach((reports: IReportDetail) => {
            reports.system_readiness.checks.forEach(check => {
                switch (check.state) {
                    case UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS:
                        this.systemReadinessSummaryDetails.success++;
                        break;
                    case UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING:
                        this.systemReadinessSummaryDetails.warnings++;
                        break;
                    case UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR:
                        this.systemReadinessSummaryDetails.errors++;
                        break;
                }
            });

            if (reports.system_readiness.duration > this.systemReadinessSummaryDetails.totalTime) {
                this.systemReadinessSummaryDetails.totalTime = reports.system_readiness.duration;
            }
        });

        this.systemReadinessSummaryDetails.maxProgressBarValue =
            this.getSummaryMaxValue(this.systemReadinessSummaryDetails);
    }

    /**
     * Set the summary of the dry run report.
     * The summary includes the number of success, warnings and errors and the total time taken.
     */
    private setDryRunSummary(systemReport: TItemConfig): void {
        systemReport?.dryrun_info?.upgrade_events.forEach((event: IEventMap) => {
            const { sub_events: subEvents } = event;

            const state = this.getDryRunStatus(subEvents[0]);

            switch (state) {
                case UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS:
                    this.dryRunSummaryDetails.success++;
                    break;
                case UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING:
                    this.dryRunSummaryDetails.warnings++;
                    break;
                case UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR:
                    this.dryRunSummaryDetails.errors++;
                    break;
            }
        });

        this.dryRunSummaryDetails.totalTime = systemReport?.dryrun_info?.duration ?? 0;
        this.dryRunSummaryDetails.maxProgressBarValue = this.getSummaryMaxValue(this.dryRunSummaryDetails);
    }

    /**
     * Set the summary of the system task report.
     * The summary includes the number of success, warnings and errors and the total time taken.
     */
    private setSystemTaskSummary(systemReport: TItemConfig): void {
        systemReport?.tasks?.forEach((check: IReportTask) => {
            if (check.summary) {
                if (check.summary?.status === TASK_SUCCESS_STATUS) {
                    this.systemTaskDetails.success++;
                } else {
                    this.systemTaskDetails.errors++;
                }

                this.systemTaskDetails.totalTime += check.summary.duration;
            }
        });

        this.systemTaskDetails.maxProgressBarValue =
            this.getSummaryMaxValue(this.systemTaskDetails);
    }

    /**
     * Calculate the maximum value of the summary of the system report.
     * The maximum value is the sum of the number of errors, warnings and success.
     */
    private getSummaryMaxValue(systemReportSummary: ISystemReportSummary): number {
        return systemReportSummary.errors +
            systemReportSummary.warnings +
            systemReportSummary.success;
    }

    /**
     * Return the total time difference between the start time and end time in milliseconds.
     */
    private getTotalTime(startTime: string, endTime: string): number {
        return utc(endTime).diff(utc(startTime), MILLISECONDS) ?? 0;
    }

    private getDryRunStatus(event: IUpgradeEvent): UpgradeFsmState {
        if (!event.end_time) {
            return UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS;
        }

        return event.status ?
            UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS : UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR;
    }
}
